import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "../../../shared/contexts/LanguageContext";
import { useProgressWithAutoRefresh } from "../../../shared/contexts/ProgressContext";
import ResponsiveFormLayout from "../../../shared/components/layout/ResponsiveFormLayout";
import CooperationLoader from "../../../shared/components/feedback/CooperationLoader";
import { CorrectionField, ActiveRequest } from "../api/ClinicApiService";
import { isMockEnabled } from "../../../shared/dev/mockData";
import LocationPicker from "../../../shared/components/ui/LocationPicker";

// Static option imports
import { Sex } from "../../../shared/types/models/Sex";
import { Language } from "../../../shared/types/models/Language";
import { MedicalDegree, MEDICAL_DEGREE_VALUES } from "../../../shared/types/models/MedicalDegree";
import { CareerProgression, CAREER_PROGRESSION_VALUES } from "../../../shared/types/models/CareerProgression";
import { EmploymentStatus, EMPLOYMENT_STATUS_VALUES } from "../../../shared/types/models/EmploymentStatus";
import { Ministry, MINISTRY_VALUES } from "../../../shared/types/models/Ministries";
import { Governorate } from "../../../shared/types/models/governorate";
import { RegistrationApiService } from "../../registration/api/RegistrationApiService";
import { AuthApiService } from "../../auth/api/AuthApiService";
import { useUnsavedFormGuard } from "../../../shared/utils/unsavedChanges";
import { ACCEPT_IMAGES, checkUploadFile, uploadProblemKey } from "../../../shared/utils/uploadPolicy";
import { apiRequest } from "../../../v2/lib/api";
import {
  CorrectionsPayload,
  DoctorRound,
  RoundItem,
  buildLegacyResubmitBody,
  buildResubmitBody,
  buildSaveBody,
  classifyError,
  correctionsPath,
  invalidValueText,
  isEmailItem,
  kindOf,
  looksLikeEmail,
  problemText,
  resubmitPath,
  saveItemPath,
  uploadDocumentType,
  validateAnswer,
} from "../../../v2/lib/corrections";

/**
 * Corrections round rules (backend docs/corrections-api.md), kept in the classic design without a redesign:
 * - Only the flagged items are sent; every item must be answered before resubmitting.
 * - A flagged document is answered by uploading exactly that type to the item's own endpoint; a document
 *   already uploaded in this round (on another device or in the new design) counts and needs no new file.
 * - «حفظ دون إرسال» saves the answers on the server (PUT per item) without resubmitting;
 *   «إعادة الإرسال» saves them and then resubmits the round (round_id + version). A 409 reloads the round.
 * - Servers without the round API fall back to the legacy flat resubmit with the flagged keys only.
 */

// Types for dynamic options
interface SelectOption {
  value: string;
  label: string;
}

interface DynamicOptions {
  specializations: SelectOption[];
  practitioners: SelectOption[];
  universities: SelectOption[];
  hospitals: SelectOption[];
  organizations: SelectOption[];
  areas: SelectOption[];
}

const CorrectionsScreen: React.FC = () => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [documentFiles, setDocumentFiles] = useState<Record<string, File | null>>({});
  // Ask before a design switch discards typed changes (both designs share this guard).
  useUnsavedFormGuard({ formData, documentFiles });
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [resubmitted, setResubmitted] = useState(false);
  const [round, setRound] = useState<DoctorRound | null>(null);
  const [roundChecked, setRoundChecked] = useState(false);
  // Bring the save / error message into view (the buttons sit at the bottom of a long form).
  useEffect(() => {
    if (notice || error) document.getElementById(notice ? "corrections-notice" : "corrections-error")?.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [notice, error]);
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  // Dynamic options state
  const [dynamicOptions, setDynamicOptions] = useState<DynamicOptions>({
    specializations: [],
    practitioners: [],
    universities: [],
    hospitals: [],
    organizations: [],
    areas: [],
  });
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [isLocationPickerOpen, setIsLocationPickerOpen] = useState(false);

  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const { progress, isLoading, refreshProgress } = useProgressWithAutoRefresh();

  // Find the request that needs corrections (any type with changes_requested status)
  const requestWithCorrections: ActiveRequest | undefined = useMemo(() => {
    return progress?.active_requests?.find(
      (r) => r.status === "changes_requested" && (r.corrections?.corrections?.length ?? 0) > 0
    );
  }, [progress]);

  const corrections = requestWithCorrections?.corrections?.corrections || [];
  const L = (ar: string, en: string) => (isRTL ? ar : en);
  const generalNotes = requestWithCorrections?.corrections?.corrections_general_notes;
  const requestId = requestWithCorrections?.request_id;
  const requestType = requestWithCorrections?.request_type;

  // Separate text, document, and location corrections
  const locationCorrection = corrections.find((c) => c.field_name === "location_coordinates") || null;
  const textCorrections = corrections.filter((c) => c.field_type !== "document" && c.field_name !== "location_coordinates");
  const documentCorrections = corrections.filter((c) => c.field_type === "document");

  // The open round (ids, version, what the server already holds). Null on servers without the round API.
  const loadRound = React.useCallback(async (): Promise<DoctorRound | null> => {
    if (!requestId || !requestType || isMockEnabled()) {
      setRoundChecked(true);
      return null;
    }
    const res = await apiRequest<CorrectionsPayload>(correctionsPath(requestType, requestId));
    const r = res.ok ? res.data?.round ?? null : null;
    setRound(r);
    setRoundChecked(true);
    return r;
  }, [requestId, requestType]);

  useEffect(() => {
    loadRound();
  }, [loadRound]);

  const itemFor = (c: CorrectionField & { id?: string }): RoundItem | undefined =>
    round?.items.find((i) => (c.id ? i.id === c.id : i.field_name === c.field_name && kindOf(i) === kindOf({ kind: (c as any).kind, field_type: c.field_type, field_name: c.field_name } as RoundItem)));
  // The input type comes from the server (derived from the column the item writes); the round item is the
  // source of truth, the /progress mirror carries the same value.
  const fieldTypeOf = (c: CorrectionField): string | undefined => itemFor(c)?.field_type || c.field_type;
  const isExempt = (c: CorrectionField) => itemFor(c)?.doctor_writable === false || (c as any).doctor_writable === false;
  const docAlreadyUploaded = (c: CorrectionField) => !!itemFor(c)?.addressed;

  // Answers the server already holds for this round take precedence over the value on record.
  // Only answers that changed on the server since we last applied them are copied in: the round is
  // reloaded after every failed save/upload too, and re-applying unchanged saved answers there would
  // silently undo what the doctor typed or picked on the map since the last save.
  const appliedSaved = useRef<Record<string, string>>({});
  useEffect(() => {
    if (!round) return;
    const changed = round.items.filter((i) => appliedSaved.current[i.id] !== JSON.stringify(i.saved_value ?? null));
    changed.forEach((i) => (appliedSaved.current[i.id] = JSON.stringify(i.saved_value ?? null)));
    if (changed.length === 0) return;
    setFormData((prev) => {
      const next = { ...prev };
      changed.forEach((i) => {
        const sv = i.saved_value;
        if (!sv) return;
        if (kindOf(i) === "location") {
          if (sv.latitude != null && sv.longitude != null) {
            next.latitude = String(sv.latitude);
            next.longitude = String(sv.longitude);
          }
        } else if (sv.value !== null && sv.value !== undefined && sv.value !== "") {
          next[i.field_name] = sv.value;
        }
      });
      return next;
    });
  }, [round]);

  // Initialize form data with current values
  useEffect(() => {
    const initialData: Record<string, any> = {};
    textCorrections.forEach((c: CorrectionField) => {
      if (c.current_value !== undefined) {
        initialData[c.field_name] = c.current_value;
      }
    });
    // Initialize location fields (current_value may have lat/lng)
    if (locationCorrection?.current_value) {
      const cv = locationCorrection.current_value;
      initialData.latitude = String(cv.latitude ?? cv.lat ?? "");
      initialData.longitude = String(cv.longitude ?? cv.lng ?? "");
      initialData.location_address = cv.address || null;
    }
    if (Object.keys(initialData).length > 0) {
      // Values on record only fill what is still empty: a progress refresh (on mount, after a 409)
      // must not replace answers already saved on the server or typed since.
      setFormData((prev) => ({ ...initialData, ...prev }));
    }
  }, [corrections]);

  // Redirect if no request needs corrections
  useEffect(() => {
    if (!isLoading && !requestWithCorrections && !resubmitted) {
      navigate("/home", { replace: true });
    }
  }, [isLoading, requestWithCorrections, navigate, resubmitted]);

  const handleFieldChange = (fieldName: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
    if (error) setError(null);
  };

  const handleFileChange = (key: string, file: File | null) => {
    const problem = file ? checkUploadFile(file, { allowPdf: false }) : null;
    if (problem) {
      setError(t(uploadProblemKey(problem, false)));
      return;
    }
    setDocumentFiles((prev) => ({
      ...prev,
      [key]: file,
    }));
    if (error) setError(null);
  };

  // Get upload endpoint based on target and request type
  const getUploadEndpoint = (baseUrl: string, reqType: string, reqId: string, target?: string): string => {
    // Doctor profile documents use the profile endpoint
    if (target === "doctor") {
      return `${baseUrl}/clinic/doctor/profile/documents/upload`;
    }

    // Request attachments use request-specific endpoints
    switch (reqType) {
      case "closure":
        return `${baseUrl}/clinic/doctor/closure/${reqId}/attachments/upload`;
      case "onboarding":
        return `${baseUrl}/clinic/doctor/onboarding/${reqId}/attachments/upload`;
      case "registration":
      default:
        return `${baseUrl}/clinic/doctor/registration/${reqId}/attachments/upload`;
    }
  };

  // Upload a single document
  const uploadDocument = async (
    fieldName: string,
    file: File,
    baseUrl: string,
    uploadKey: string,
    target?: string,
    endpointOverride?: string | null
  ): Promise<{ ok: boolean; message?: string; status?: number }> => {
    if (isMockEnabled()) {
      console.log(`[Mock] uploadDocument: ${fieldName} - ${file.name}`);
      setUploadProgress((prev) => ({ ...prev, [uploadKey]: 100 }));
      return { ok: true };
    }

    const authApi = AuthApiService.getInstance();
    const accessToken = authApi.getAccessToken();
    if (!accessToken) return { ok: false, status: 401 };

    return new Promise((resolve) => {
      const xhr = new XMLHttpRequest();
      const uploadFormData = new FormData();
      uploadFormData.append("file", file, file.name);
      uploadFormData.append("document_type", fieldName);

      xhr.upload.addEventListener("progress", (e) => {
        if (e.lengthComputable) {
          const percent = Math.round((e.loaded / e.total) * 100);
          setUploadProgress((prev) => ({ ...prev, [uploadKey]: percent }));
        }
      });

      xhr.onload = async () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploadProgress((prev) => ({ ...prev, [uploadKey]: 100 }));
          resolve({ ok: true });
        } else if (xhr.status === 401 || xhr.status === 444) {
          const refreshResult = await authApi.refreshAccessToken();
          if (refreshResult.isSuccess) {
            resolve(uploadDocument(fieldName, file, baseUrl, uploadKey, target, endpointOverride));
          } else {
            resolve({ ok: false, status: xhr.status });
          }
        } else {
          let message: string | undefined;
          try {
            const body = JSON.parse(xhr.responseText);
            message = body?.error?.message || (typeof body?.detail === "string" ? body.detail : undefined);
          } catch {
            /* not JSON */
          }
          resolve({ ok: false, status: xhr.status, message });
        }
      };

      xhr.onerror = () => resolve({ ok: false, status: 0 });
      xhr.ontimeout = () => resolve({ ok: false, status: 0 });

      const endpoint = endpointOverride ? `${baseUrl}${endpointOverride}` : getUploadEndpoint(baseUrl, requestType!, requestId!, target);
      xhr.open("POST", endpoint);
      xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
      xhr.timeout = 60000;
      xhr.send(uploadFormData);
    });
  };

  // Dynamic fields that require API fetching
  const dynamicFieldNames = [
    "specialization_id",
    "doctor_specialization_id",
    "practition_id",
    "doctor_practition_id",
    "university_id",
    "doctor_university_id",
    "graduated_from_id",
    "doctor_graduated_from_id",
    "hospital_id",
    "doctor_hospital_id",
    "organization_id",
    "doctor_organization_id",
    "area_id"
  ];

  // Check if any correction needs dynamic options
  const needsDynamicOptions = useMemo(() => {
    return corrections.some(
      (c) => c.field_type === "select" && dynamicFieldNames.includes(c.field_name)
    );
  }, [corrections]);

  // Fetch dynamic options when needed
  useEffect(() => {
    const fetchDynamicOptions = async () => {
      if (!needsDynamicOptions) return;

      setLoadingOptions(true);
      const registrationApi = new RegistrationApiService();

      try {
        const selectFieldNames = corrections
          .filter((c) => c.field_type === "select")
          .map((c) => c.field_name);

        const newOptions: Partial<DynamicOptions> = {};

        // Fetch specializations if needed
        if (
          selectFieldNames.some((f) =>
            ["specialization_id", "doctor_specialization_id"].includes(f)
          )
        ) {
          const specsResult = await registrationApi.getSpecializations(1, 100);
          if (specsResult.isSuccess && specsResult.data) {
            newOptions.specializations = specsResult.data.map((s) => ({
              value: String(s.id),
              label: isRTL ? s.name_ar || s.name : s.name,
            }));
          }
        }

        // Fetch practitioners if needed
        if (
          selectFieldNames.some((f) =>
            ["practition_id", "doctor_practition_id"].includes(f)
          )
        ) {
          const pracsResult = await registrationApi.getPractitioners(1, 100);
          if (pracsResult.isSuccess && pracsResult.data) {
            newOptions.practitioners = pracsResult.data.map((p) => ({
              value: String(p.id),
              label: isRTL ? p.name_ar || p.name : p.name,
            }));
          }
        }

        // Fetch universities if needed. graduated_from_id is included because that is
        // the name the admin panel flags "Graduated From" under, and the column is
        // constrained to clinic_universities (every populated value is a university,
        // and the label reads جامعة التخرج) despite the ORM declaring a College FK.
        if (
          selectFieldNames.some((f) =>
            ["university_id", "doctor_university_id",
             "graduated_from_id", "doctor_graduated_from_id"].includes(f)
          )
        ) {
          const unisResult = await registrationApi.getUniversities(1, 100);
          if (unisResult.isSuccess && unisResult.data) {
            newOptions.universities = unisResult.data.map((u) => ({
              value: String(u.id),
              label: isRTL ? u.name_ar || u.name : u.name,
            }));
          }
        }

        // Fetch organizations if needed (filtered by ministry)
        if (
          selectFieldNames.some((f) =>
            ["organization_id", "doctor_organization_id"].includes(f)
          )
        ) {
          // Get current ministry value to filter organizations
          const ministryCorrection = corrections.find((c) =>
            ["ministry", "doctor_ministry"].includes(c.field_name)
          );
          const currentMinistry = formData.ministry || formData.doctor_ministry || ministryCorrection?.current_value;

          const orgsResult = await registrationApi.getOrganizations(1, 100, false, currentMinistry);
          if (orgsResult.isSuccess && orgsResult.data) {
            newOptions.organizations = orgsResult.data.map((o) => ({
              value: String(o.id),
              label: isRTL ? o.name_ar || o.name : o.name,
            }));
          }
        }

        // Fetch hospitals if needed (filtered by organization)
        if (
          selectFieldNames.some((f) =>
            ["hospital_id", "doctor_hospital_id"].includes(f)
          )
        ) {
          // Get current organization_id to filter hospitals
          const orgCorrection = corrections.find((c) =>
            ["organization_id", "doctor_organization_id"].includes(c.field_name)
          );
          const currentOrgId = formData.organization_id || formData.doctor_organization_id || orgCorrection?.current_value;

          const hospsResult = await registrationApi.getHospitals(currentOrgId);
          if (hospsResult.isSuccess && hospsResult.data) {
            newOptions.hospitals = hospsResult.data.map((h) => ({
              value: String(h.id),
              label: isRTL ? h.name_ar || h.name : h.name,
            }));
          }
        }
        // Fetch Areas if needed
        if (
          selectFieldNames.some((f) => f === "area_id")
        ) {
          const areaResult = await registrationApi.getAreas(1, 200, false, requestWithCorrections?.governorate);
          if (areaResult.isSuccess && areaResult.data) {
            newOptions.areas = areaResult.data.map((p) => ({
              value: String(p.id),
              label: isRTL ? p.name_ar || p.name : p.name,
            }));
          }
        } 
        setDynamicOptions((prev) => ({ ...prev, ...newOptions }));
      } catch (err) {
        console.error("Failed to fetch dynamic options:", err);
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchDynamicOptions();
  }, [needsDynamicOptions, corrections, isRTL]);

  // Get options for a select field
  const getOptionsForField = (fieldName: string): SelectOption[] | null => {
    // Static options
    const staticOptionsMap: Record<string, SelectOption[]> = {
      // Sex
      sex: [
        { value: Sex.MALE, label: t("male") || "Male" },
        { value: Sex.FEMALE, label: t("female") || "Female" },
      ],
      doctor_sex: [
        { value: Sex.MALE, label: t("male") || "Male" },
        { value: Sex.FEMALE, label: t("female") || "Female" },
      ],

      // Language
      language: Object.values(Language).map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      doctor_language: Object.values(Language).map((v) => ({
        value: v,
        label: t(v) || v,
      })),

      // Medical Degree
      medical_degree: MEDICAL_DEGREE_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      doctor_medical_degree: MEDICAL_DEGREE_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),

      // Career Progression
      career_progression: CAREER_PROGRESSION_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      doctor_career_progression: CAREER_PROGRESSION_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),

      // Employment Status
      employment_status: EMPLOYMENT_STATUS_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      doctor_employment_status: EMPLOYMENT_STATUS_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),

      // Ministry
      ministry: MINISTRY_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      doctor_ministry: MINISTRY_VALUES.map((v) => ({
        value: v,
        label: t(v) || v,
      })),

      // Governorate - translation keys match exact values like "Baghdad", "Al Anbar"
      governorate: Object.values(Governorate).map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      doctor_governorate: Object.values(Governorate).map((v) => ({
        value: v,
        label: t(v) || v,
      })),
      clinic_governorate: Object.values(Governorate).map((v) => ({
        value: v,
        label: t(v) || v,
      })),

      // نوع الطبيب. Labels reuse the profile screen's keys; "higher_diploma" is
      // shared with the medical-degree list, which has identical wording.
      doctor_type: [
        { value: "specialist", label: t("specialist") },
        { value: "practitioner", label: t("practitioner") },
        { value: "higher_diploma", label: t("higher_diploma") },
      ],

      // Clinic work hours
      clinic_work_hours: [
        { value: "morning", label: t("morning") || "Morning" },
        { value: "evening", label: t("evening") || "Evening" },
      ],
    };

    // Check static options first
    if (staticOptionsMap[fieldName]) {
      return staticOptionsMap[fieldName];
    }

    // Check dynamic options
    if (
      ["specialization_id", "doctor_specialization_id"].includes(fieldName) &&
      dynamicOptions.specializations.length > 0
    ) {
      return dynamicOptions.specializations;
    }

    if (
      ["practition_id", "doctor_practition_id"].includes(fieldName) &&
      dynamicOptions.practitioners.length > 0
    ) {
      return dynamicOptions.practitioners;
    }

    if (
      ["university_id", "doctor_university_id"].includes(fieldName) &&
      dynamicOptions.universities.length > 0
    ) {
      return dynamicOptions.universities;
    }

    if (
      ["graduated_from_id", "doctor_graduated_from_id"].includes(fieldName) &&
      dynamicOptions.universities.length > 0
    ) {
      return dynamicOptions.universities;
    }

    if (
      ["hospital_id", "doctor_hospital_id"].includes(fieldName) &&
      dynamicOptions.hospitals.length > 0
    ) {
      return dynamicOptions.hospitals;
    }

    if (
      ["organization_id", "doctor_organization_id"].includes(fieldName) &&
      dynamicOptions.organizations.length > 0
    ) {
      return dynamicOptions.organizations;
    }
    if (
      ["area_id"].includes(fieldName) &&
      dynamicOptions.areas.length > 0
    ) {
      return dynamicOptions.areas;
    }

    return null;
  };

  // Get the appropriate resubmit endpoint based on request type
  const getResubmitEndpoint = (baseUrl: string, reqType: string, reqId: string): string => {
    switch (reqType) {
      case "closure":
        return `${baseUrl}/clinic/doctor/closure/${reqId}/resubmit`;
      case "onboarding":
        return `${baseUrl}/clinic/doctor/onboarding/${reqId}/resubmit`;
      case "registration":
      default:
        return `${baseUrl}/clinic/doctor/registration/${reqId}/resubmit`;
    }
  };

  const errText = (e: { message?: string; status?: number } | undefined, fallback: string) =>
    e?.status === 0 ? L("تعذّر الاتصال بالخادم. تحقق من الإنترنت ثم أعد المحاولة.", "Could not reach the server. Check your connection and retry.") : e?.message || fallback;

  /**
   * mode "save": uploads the chosen files and saves every answer on the server, without resubmitting.
   * mode "resubmit": the same, then resubmits the round.
   */
  const handleSubmit = async (mode: "save" | "resubmit" = "resubmit") => {
    if (!requestId || !requestType) return;

    setSubmitting(true);
    setError(null);
    setNotice(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
      const current = round || (roundChecked ? null : await loadRound());

      // 1. Upload the chosen files (the flagged type only, to the item's own endpoint).
      for (let i = 0; i < documentCorrections.length; i++) {
        const correction = documentCorrections[i];
        const key = `doc_${i}`;
        const file = documentFiles[key];
        if (!file) continue;
        const item = itemFor(correction);
        const docType = item ? uploadDocumentType(item) : correction.field_name;
        const result = await uploadDocument(docType, file, baseUrl, key, correction.target, item?.upload_endpoint || (correction as any).upload_endpoint);
        if (!result.ok) {
          if (result.status === 409) await loadRound();
          throw new Error(errText(result, `${t("failed_to_upload_document") || "Failed to upload"}: ${correction.field_label || correction.field_name}`));
        }
        setDocumentFiles((prev) => ({ ...prev, [key]: null }));
      }

      if (isMockEnabled()) {
        console.log("[Mock] resubmit corrections", formData);
      } else if (current) {
        // 2. Save each flagged field/location answer on the server.
        for (const item of current.items) {
          const kind = kindOf(item);
          if (kind === "document" || item.doctor_writable === false) continue;
          const value = kind === "location" ? (formData.latitude && formData.longitude ? { lat: String(formData.latitude), lng: String(formData.longitude) } : null) : formData[item.field_name];
          const problem = validateAnswer(item, value ?? null);
          if (problem === "required") continue; // left empty: resubmit reports it as missing
          if (problem) throw new Error(`${item.label || item.field_name}: ${problemText(problem, isRTL ? "ar" : "en")}`);
          const res = await apiRequest(saveItemPath(requestType, requestId, item.id), { method: "PUT", body: buildSaveBody(current, item, value ?? null) });
          if (!res.ok) {
            const err = classifyError(res.error);
            if (err.kind === "invalid") {
              // 422 corrections.invalid_value (e.g. an e-mail that is not an address, a graduation year out of range).
              throw new Error(`${item.label || item.field_name}: ${invalidValueText(err.itemErrors[item.id] || res.error.message, isRTL ? "ar" : "en")}`);
            }
            if (err.kind === "stale" || err.kind === "not_flagged" || err.kind === "not_editable") {
              await loadRound();
              await refreshProgress(true);
              throw new Error(L("تغيّرت التعديلات المطلوبة على الخادم فحدّثنا الصفحة. ما حفظته ما زال محفوظاً؛ راجع البنود ثم أعد المحاولة.", "The requested corrections changed on the server, so the page was refreshed. What you saved is kept; check the items and try again."));
            }
            throw new Error(res.error.message || t("failed_to_submit_corrections"));
          }
        }
        if (mode === "save") {
          await loadRound();
          setNotice(L("تم حفظ التغييرات. لم يُعد إرسال الطلب بعد — اضغط «إعادة الإرسال» عندما تنتهي.", "Changes saved. The request has not been resubmitted yet — press «Resubmit» when you are done."));
          return;
        }
        // 3. Resubmit the round.
        const res = await apiRequest(resubmitPath(requestType, requestId), { method: "POST", body: buildResubmitBody(current) });
        if (!res.ok) {
          const err = classifyError(res.error);
          if (err.kind === "stale" || err.kind === "incomplete" || err.kind === "not_flagged" || err.kind === "not_editable") {
            await loadRound();
            await refreshProgress(true);
          }
          if (err.kind === "incomplete") throw new Error(L("لم تُنجز كل البنود بعد. أكمل البنود المطلوبة ثم أعد الإرسال.", "Not every item is done yet. Finish the requested items, then resubmit."));
          if (err.kind === "stale") throw new Error(L("تغيّرت التعديلات المطلوبة على الخادم فحدّثنا الصفحة. ما حفظته ما زال محفوظاً؛ أعد المحاولة.", "The requested corrections changed on the server, so the page was refreshed. What you saved is kept; please try again."));
          if (err.kind === "invalid") {
            const [badId, badMsg] = Object.entries(err.itemErrors)[0] || [];
            const bad = current.items.find((i) => i.id === badId);
            const text = invalidValueText(badMsg || res.error.message, isRTL ? "ar" : "en");
            throw new Error(bad ? `${bad.label || bad.field_name}: ${text}` : text);
          }
          throw new Error(res.error.message || t("failed_to_submit_corrections"));
        }
      } else {
        // Server without the round API: legacy flat resubmit with the flagged keys only.
        if (mode === "save") {
          setNotice(L("الحفظ دون إرسال غير متاح حالياً.", "Saving without sending is currently unavailable."));
          return;
        }
        const payload = buildLegacyResubmitBody(
          corrections as any[],
          { ...formData, location_coordinates: formData.latitude && formData.longitude ? { lat: String(formData.latitude), lng: String(formData.longitude) } : null },
          requestType
        );
        const res = await apiRequest(getResubmitEndpoint("", requestType, requestId), { method: "POST", body: payload });
        if (!res.ok) throw new Error(res.error.message || t("failed_to_submit_corrections"));
      }

      setResubmitted(true);
      await refreshProgress(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t("failed_to_submit_corrections"));
      // Uploads and saves that did succeed are on the server: show them as done.
      if (round) loadRound();
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-white flex justify-center items-center">
        <CooperationLoader />
      </div>
    );
  }

  if (resubmitted) {
    return (
      <ResponsiveFormLayout
        onBack={() => navigate("/home")}
        showBackButton={true}
        showLanguageToggle={true}
        icon="✅"
        iconSize="w-24 h-24 text-6xl"
        title={L("أُعيد إرسال طلبك للمراجعة", "Your request was resubmitted for review")}
        subtitle={L(
          "يعود طلبك إلى المراجعة، وسيتحقق الموظف من كل تعديل. قد يُطلب منك تصحيح جديد إذا بقي أي بند غير صحيح.",
          "Your request goes back to review and staff check each correction. You may be asked again if an item is still wrong."
        )}
        actions={
          <button
            onClick={() => navigate("/home", { replace: true })}
            className="w-full min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold"
            style={{ backgroundColor: "#2E5D7D" }}
          >
            {t("home") !== "home" ? t("home") : L("الرئيسية", "Home")}
          </button>
        }
        maxHeight="90vh"
      >
        <div />
      </ResponsiveFormLayout>
    );
  }

  if (!requestWithCorrections || corrections.length === 0) {
    return null; // Will redirect via useEffect
  }

  // Check if all required corrections are filled
  // Identity-protected items of old rounds are exempt; a document already uploaded in this round counts.
  const filled = (v: any) => v !== undefined && v !== null && String(v).trim() !== "";
  const allTextFieldsFilled = textCorrections.every((c) => isExempt(c) || filled(formData[c.field_name]));
  const allDocumentsSelected = documentCorrections.every((c, i) => isExempt(c) || documentFiles[`doc_${i}`] || docAlreadyUploaded(c));
  const locationFilled = !locationCorrection || isExempt(locationCorrection) || (formData.latitude && formData.longitude);
  const canSubmit = (textCorrections.length === 0 || allTextFieldsFilled) &&
                    (documentCorrections.length === 0 || allDocumentsSelected) &&
                    locationFilled;

  const submitButton = (
    <div className="w-full space-y-3">
    {round ? (
      <button
        onClick={() => handleSubmit("save")}
        disabled={submitting}
        className="w-full min-h-[48px] px-6 py-3 rounded-2xl text-base font-semibold border-2 transition-all duration-200 disabled:opacity-50"
        style={{ borderColor: "#2E5D7D", color: "#2E5D7D" }}
      >
        {L("حفظ دون إرسال", "Save without sending")}
      </button>
    ) : null}
    <button
      onClick={() => handleSubmit("resubmit")}
      disabled={submitting || !canSubmit}
      className={`w-full min-h-[56px] px-6 py-4 rounded-2xl text-white text-base font-semibold transition-all duration-200 flex items-center justify-center gap-3 ${
        submitting || !canSubmit
          ? "opacity-50 cursor-not-allowed"
          : "hover:opacity-90 cursor-pointer shadow-md hover:shadow-lg"
      }`}
      style={{ backgroundColor: "#2E5D7D" }}
    >
      {submitting ? (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
          <span>{t("submitting") || "Submitting..."}</span>
        </div>
      ) : (
        <span>{t("resubmit") || "Resubmit"}</span>
      )}
    </button>
    </div>
  );

  return (
    <ResponsiveFormLayout
      onBack={() => navigate("/home")}
      showBackButton={true}
      showLanguageToggle={true}
      icon="⚠️"
      iconSize="w-24 h-24 text-6xl"
      title={t("corrections_required") || "Corrections Required"}
      subtitle={t("corrections_required_message") || "Please review and update the flagged fields."}
      actions={submitButton}
      maxHeight="90vh"
    >
      <div className="w-full space-y-6">
        {/* Error Message */}
        {error && (
          <div id="corrections-error" role="alert" className="w-full rounded-xl p-4 bg-red-50 border border-red-200">
            <div className="flex items-start gap-3">
              <span className="text-red-500 text-lg">⚠️</span>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {notice && (
          <div id="corrections-notice" className="w-full rounded-xl p-4 bg-green-50 border border-green-200" role="status" aria-live="polite">
            <p className="text-sm text-green-800">{notice}</p>
          </div>
        )}

        {/* General Notes from Reviewer */}
        {generalNotes && (
          <div className="p-4 rounded-xl bg-orange-50 border border-orange-200">
            <div className="flex items-start gap-3">
              <span className="text-orange-500 text-2xl">📝</span>
              <div>
                <h4 className="font-semibold text-orange-800 mb-1">
                  {t("reviewer_notes") || "Reviewer Notes"}
                </h4>
                <p className="text-sm text-orange-700">{generalNotes}</p>
              </div>
            </div>
          </div>
        )}

        {/* Request Info */}
        <div className="p-4 rounded-xl bg-gray-50 border border-gray-200">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">{t("request_number") || "Request Number"}:</span>
            <span className="text-sm font-semibold text-gray-900">
              {requestWithCorrections.request_number}
            </span>
          </div>
        </div>

        {/* Document Corrections */}
        {documentCorrections.length > 0 && (
          <section className="space-y-4">
            <h3 className={`text-base font-semibold text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              {t("documents_to_reupload") || "Documents to Re-upload"}
            </h3>

            {documentCorrections.map((correction: CorrectionField, index: number) => {
              const key = `doc_${index}`;
              return (
              <div
                key={key}
                className="p-4 rounded-xl border border-orange-200 bg-white"
              >
                <label
                  className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? "text-right" : "text-left"}`}
                >
                  {correction.field_label || correction.field_name}
                  <span className="text-orange-500 ml-1">*</span>
                </label>
                <p className={`text-xs text-orange-600 mb-3 ${isRTL ? "text-right" : "text-left"}`}>
                  {correction.reason}
                </p>
                {docAlreadyUploaded(correction) && !documentFiles[key] ? (
                  <p className={`text-xs text-green-700 mb-2 ${isRTL ? "text-right" : "text-left"}`}>
                    {L("تم رفع ملف جديد لهذا البند وحفظه. يمكنك استبداله باختيار ملف آخر.", "A new file was uploaded and saved for this item. You can replace it by choosing another file.")}
                  </p>
                ) : null}

                <input
                  type="file"
                  accept={ACCEPT_IMAGES}
                  ref={(el) => (fileInputRefs.current[key] = el)}
                  onChange={(e) => handleFileChange(key, e.target.files?.[0] || null)}
                  className="hidden"
                />

                {documentFiles[key] ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <span className="text-green-600">✓</span>
                        <span className="text-sm text-green-800 truncate max-w-[200px]">
                          {documentFiles[key]?.name}
                        </span>
                      </div>
                      <button
                        onClick={() => handleFileChange(key, null)}
                        className="text-red-500 text-sm hover:text-red-700"
                      >
                        {t("remove") || "Remove"}
                      </button>
                    </div>
                    {uploadProgress[key] > 0 && uploadProgress[key] < 100 && (
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#2E5D7D] h-2 rounded-full transition-all"
                          style={{ width: `${uploadProgress[key]}%` }}
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRefs.current[key]?.click()}
                    className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-[#2E5D7D] hover:text-[#2E5D7D] transition-colors flex items-center justify-center gap-2"
                  >
                    <span className="text-xl">📷</span>
                    <span>{t("select_file") || "Select File"}</span>
                    <span className="text-xs text-gray-400">{t("png_jpg_up_to_10mb")}</span>
                  </button>
                )}
              </div>
              );
            })}
          </section>
        )}

        {/* Location Correction */}
        {locationCorrection && (
          <div className="p-4 rounded-xl border border-orange-200 bg-white">
            <label
              className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? "text-right" : "text-left"}`}
            >
              {locationCorrection.field_label || t("clinic_location") || "Location"}
              <span className="text-orange-500 ml-1">*</span>
            </label>
            <p className={`text-xs text-orange-600 mb-3 ${isRTL ? "text-right" : "text-left"}`}>
              {locationCorrection.reason}
            </p>

            {formData.latitude && formData.longitude ? (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="text-sm text-green-800 font-medium mb-2 flex items-center gap-2">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t("location_selected") || "Location Selected"}
                      </div>
                      {formData.location_address ? (
                        <div className="text-sm text-gray-800">
                          <p className="font-medium mb-1">{formData.location_address}</p>
                          <p className="text-xs text-gray-500 font-mono">
                            {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                          </p>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600 font-mono">
                          {parseFloat(formData.latitude).toFixed(6)}, {parseFloat(formData.longitude).toFixed(6)}
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((prev) => ({
                          ...prev,
                          latitude: null,
                          longitude: null,
                          location_address: null,
                        }));
                      }}
                      className="text-red-500 hover:text-red-700 transition-colors p-1"
                      title={t("clear_location") || "Clear location"}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsLocationPickerOpen(true)}
                  className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  <span>{t("change_location") || "Change Location"}</span>
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setIsLocationPickerOpen(true)}
                className="w-full px-4 py-3 border-2 border-dashed border-gray-300 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 text-gray-600 hover:text-blue-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t("select_location_on_map") || "Select Location on Map"}</span>
              </button>
            )}

            <LocationPicker
              value={formData.latitude && formData.longitude ? {
                lat: formData.latitude,
                lng: formData.longitude,
                address: formData.location_address || undefined
              } : null}
              onChange={(location) => {
                if (location) {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: location.lat,
                    longitude: location.lng,
                    location_address: location.address || null,
                  }));
                } else {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: null,
                    longitude: null,
                    location_address: null,
                  }));
                }
              }}
              isOpen={isLocationPickerOpen}
              onClose={() => setIsLocationPickerOpen(false)}
            />
          </div>
        )}

        {/* Text Field Corrections */}
        {textCorrections.length > 0 && (
          <section className="space-y-4">
            <h3 className={`text-base font-semibold text-gray-800 ${isRTL ? "text-right" : "text-left"}`}>
              {t("fields_to_update") || "Fields to Update"}
            </h3>

            {textCorrections.map((correction: CorrectionField) => (
              <div
                key={correction.field_name}
                className="p-4 rounded-xl border border-orange-200 bg-white"
              >
                <label
                  className={`block text-sm font-medium text-gray-700 mb-1 ${isRTL ? "text-right" : "text-left"}`}
                >
                  {correction.field_label || correction.field_name}
                  <span className="text-orange-500 ml-1">*</span>
                </label>
                <p className={`text-xs text-orange-600 mb-3 ${isRTL ? "text-right" : "text-left"}`}>
                  {correction.reason}
                </p>

                {fieldTypeOf(correction) === "date" ? (
                  <input
                    type="date"
                    value={formData[correction.field_name] || ""}
                    onChange={(e) => handleFieldChange(correction.field_name, e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl transition-colors duration-200 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                  />
                ) : fieldTypeOf(correction) === "number" ? (
                  <input
                    type="number"
                    value={formData[correction.field_name] || ""}
                    onChange={(e) => handleFieldChange(correction.field_name, parseInt(e.target.value) || "")}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl transition-colors duration-200 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                  />
                ) : fieldTypeOf(correction) === "textarea" ? (
                  <textarea
                    value={formData[correction.field_name] || ""}
                    onChange={(e) => handleFieldChange(correction.field_name, e.target.value)}
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl transition-colors duration-200 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                  />
                ) : fieldTypeOf(correction) === "select" && getOptionsForField(correction.field_name) ? (
                  <select
                    value={formData[correction.field_name] || ""}
                    onChange={(e) => handleFieldChange(correction.field_name, e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl transition-colors duration-200 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                    disabled={loadingOptions && dynamicFieldNames.includes(correction.field_name)}
                  >
                    <option value="">{t("select") || "Select"}</option>
                    {getOptionsForField(correction.field_name)?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : isEmailItem(correction) ? (
                  <>
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      dir="ltr"
                      value={formData[correction.field_name] || ""}
                      onChange={(e) => handleFieldChange(correction.field_name, e.target.value.trim())}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl transition-colors duration-200 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                    />
                    {formData[correction.field_name] && !looksLikeEmail(String(formData[correction.field_name])) ? (
                      <p className={`text-xs text-red-600 mt-1 ${isRTL ? "text-right" : "text-left"}`}>
                        {L("هذا ليس بريداً إلكترونياً صحيحاً بعد — مثال: name@example.com", "This is not a valid email address yet — for example name@example.com")}
                      </p>
                    ) : null}
                  </>
                ) : (
                  <input
                    type="text"
                    value={formData[correction.field_name] || ""}
                    onChange={(e) => handleFieldChange(correction.field_name, e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl transition-colors duration-200 focus:border-[#2E5D7D] focus:ring-2 focus:ring-[#2E5D7D]/20"
                  />
                )}
              </div>
            ))}
          </section>
        )}
      </div>
    </ResponsiveFormLayout>
  );
};

export default CorrectionsScreen;
